import Search from "@mui/icons-material/Search";
import { Checkbox, FormControlLabel, Grid, InputLabel, Select } from "@mui/material";
import { styled } from "@mui/material/styles";
import { debounce, get } from "lodash";
import React from "react";
import Autocomplete from "../../../common/js/components/Autocomplete";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { company_type_options } from "../admin/constants";
import searchConstants from "../search/constants";
import { us_states_options } from "../utils/constants";

const StyledClearLink = styled("span")(() => ({
    textTransform: "none",
    "&:hover": {
        textDecoration: "underline",
        cursor: "pointer",
    },
}));

const debounceSearchParentCompany = debounce((handleSearch, company, term, parentCompanyTypeFilter) => {
    let companyTypeFilter = get(company, `parentCompany.companyTypes`)
        ? get(company, `parentCompany.companyTypes`)
        : parentCompanyTypeFilter;
    handleSearch(term, companyTypeFilter);
}, searchConstants["DEBOUNCE_SEARCH_DELAY"]);

const CompanyForm = (props) => {
    const {
        errors,
        disableInputs,
        handleInputChange,
        parentCompanyTypeFilter,
        company,
        autocompleteResults,
        handleSearchParentCompany,
        handleChangeParentCompanyType,
    } = props;

    const handleCompanyChange = (event) => {
        handleInputChange("company." + event.target.id, event.target.value);
    };

    const handleChangeMultiple = (event) => {
        const { options } = event.target;
        const types = [];
        for (let i = 0, l = options.length; i < l; i += 1) {
            if (options[i].selected) {
                types.push(options[i].value);
            } else {
            }
        }
        handleInputChange("company.companyTypes", types);
    };

    const handleParentCompanyCheck = (value, checked) => {
        if (checked === true) {
            handleInputChange("company.parentCompany", null);
        }
        handleInputChange(value, checked);
    };

    const buildParentCompanyLabel = () => {
        if (get(company, `parentCompany`, null) !== null) {
            return [
                "Institutional Owner ",
                <StyledClearLink
                    onClick={() => {
                        handleInputChange("company.parentCompany", null);
                        handleChangeParentCompanyType("BUSINESS");
                    }}
                >
                    (Clear)
                </StyledClearLink>,
            ];
        }

        return "Institutional Owner";
    };

    const formatCompanyTypes = (arr) => {
        const companyTypesMap = {};
        const formattedCompanyTypes = [];
        company_type_options.forEach((ct) => {
            companyTypesMap[ct.value] = ct.label;
        });

        arr &&
            arr.forEach((e) => {
                formattedCompanyTypes.push(companyTypesMap[e]);
            });

        return formattedCompanyTypes.join(", ");
    };

    const parentCompanyType = get(company, `parentCompany.companyTypes`);

    return (
        <React.Fragment>
            <BSInput
                id="legalName"
                label="LEGAL NAME"
                placeholder="Enter company legal name"
                helperText={!disableInputs && "Required"}
                errorText="Required"
                error={get(errors, "company.legalName")}
                value={company && company.legalName ? company.legalName : null}
                onChange={handleCompanyChange}
                disabled={disableInputs === true}
            />
            <BSInput
                id="displayName"
                label="COMMON NAME"
                placeholder="Enter company common name"
                helperText={!disableInputs && "Required"}
                errorText="Required"
                error={get(errors, "company.displayName")}
                value={company ? company.displayName : null}
                onChange={handleCompanyChange}
                disabled={disableInputs === true}
            />
            <div style={{ display: "flex", gap: "1vw" }}>
                <div style={{ flex: "2 2 0" }}>
                    <Autocomplete
                        id="institutionalOwner"
                        label={buildParentCompanyLabel()}
                        error={get(errors, `institutionalOwner`, "")}
                        initialInputValue={company && company.parentCompany ? company.parentCompany.displayName : ""}
                        itemToString={(i) => {
                            return get(i, `displayName`, null);
                        }} //change to display
                        onSelect={(company) => handleInputChange("company.parentCompany", company)}
                        placeholder="Enter Approved Company"
                        searchFunction={(input) =>
                            debounceSearchParentCompany(
                                handleSearchParentCompany,
                                company,
                                input,
                                parentCompanyTypeFilter
                            )
                        }
                        startAdornment={<Search />}
                        suggestions={autocompleteResults}
                        disabled={disableInputs || company.parentFlg}
                        errorText="Required"
                        options={{
                            key: "companyKey",
                            display: (suggestion) => {
                                if (get(suggestion, "companyKey", "") === company.companyKey)
                                    return (
                                        <fieldset disabled={true}>
                                            {get(suggestion, "companyKey", "") +
                                                " / " +
                                                get(suggestion, "legalName", "") +
                                                " / " +
                                                get(suggestion, "displayName", "")}
                                        </fieldset>
                                    );
                                else
                                    return (
                                        get(suggestion, "companyKey", "") +
                                        " / " +
                                        get(suggestion, "legalName", "") +
                                        " / " +
                                        get(suggestion, "displayName", "")
                                    );
                            },
                        }}
                    />
                </div>
                <div style={{ flex: "1 1 0" }}>
                    <BSSelect
                        label="Company Type"
                        id="companySearchType"
                        value={parentCompanyType ? formatCompanyTypes(parentCompanyType) : parentCompanyTypeFilter}
                        options={company_type_options}
                        onChange={(e) => {
                            handleChangeParentCompanyType(e.target.value);
                            handleInputChange("company.parentCompany", null);
                        }}
                        disabled={disableInputs || company.parentFlg}
                        extraInputStyles={{ paddingTop: "1px" }}
                    />
                </div>
            </div>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={company.parentFlg ? true : false}
                        onChange={(e) => handleParentCompanyCheck("company." + e.target.value, e.target.checked)}
                        value="parentFlg"
                        disabled={!company || (company.parentCompany !== null && company.parentCompany !== "")}
                        color="primary"
                    />
                }
                label="Is Institutional Owner / Parent Company"
            />
            <Grid container spacing={8} sx={{
                alignItems: "flex-start",
                paddingTop: "20px"
            }}>
                <Grid size={{xs:6}}>
                    <div style={{ paddingBottom: "40px" }}>
                        <InputLabel id="CompanyType" shrink htmlFor="select-multiple-native">
                            Company Type
                        </InputLabel>
                        <Select
                            labelId="CompanyType"
                            multiple
                            native
                            value={company.companyTypes}
                            onChange={(e) => handleChangeMultiple(e)}
                            inputProps={{
                                id: "companyTypes",
                                placeholder: "placeholder",
                            }}
                            // Hide dropdown icon by specifying a blank icon element
                            IconComponent={() => <i></i>}
                            sx={{
                                "& .MuiNativeSelect-select": {
                                    padding: "5px 15px", // Adjust padding for the select input
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "transparent", // Set to transparent or the same color as the default
                                },
                            }}
                        >
                            {company_type_options.map((company) => (
                                <option value={company.value}>{company.label}</option>
                            ))}
                        </Select>
                    </div>
                </Grid>
                <Grid style={{ paddingTop: "20px", paddingLeft: "40px" }} size={{xs:6}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={company.approvedFlg ? true : false}
                                onChange={(e) => handleInputChange("company." + e.target.value, e.target.checked)}
                                value="approvedFlg"
                                disabled={false}
                                color="primary"
                            />
                        }
                        label="Approved by CDT"
                    />
                </Grid>
            </Grid>
            <BSInput
                id="address1"
                label="Address"
                value={get(company, `mainAddress.address1`, "")}
                error={get(errors, `mainAddress.address1`, null)}
                onChange={(e) => handleInputChange("company.mainAddress.address1", e.target.value)}
                placeholder="Enter address"
                disabled={disableInputs === true}
            />
            <div style={{ display: "flex", gap: "1vw" }}>
                <div style={{ flex: "1 1 0" }}>
                    <BSInput
                        id="city"
                        label="City"
                        value={get(company, `mainAddress.city.city`, "")}
                        onChange={(e) => handleInputChange("company.mainAddress.city.city", e.target.value)}
                        placeholder="Enter city"
                        disabled={disableInputs === true}
                    />
                </div>
                <div style={{ flex: "1 1 0" }}>
                    <BSSelect
                        id="state"
                        label="State"
                        value={get(company, `mainAddress.city.state.stateCode`, "")}
                        error={get(errors, `mainAddress.city.state.stateCode`, null)}
                        onChange={(e) => handleInputChange("company.mainAddress.city.state.stateCode", e.target.value)}
                        options={us_states_options}
                        disabled={disableInputs === true}
                    />
                </div>
                <div style={{ flex: "1 1 0" }}>
                    <BSInput
                        id="zipCode"
                        label="Zip Code"
                        value={get(company, `mainAddress.zipCode.zipCode`, "")}
                        error={get(errors, `mainAddress.zipCode.zipCode`, null)}
                        onChange={(e) => handleInputChange("company.mainAddress.zipCode.zipCode", e.target.value)}
                        placeholder="Enter zip code"
                        disabled={disableInputs === true}
                    />
                </div>
            </div>
            <BSInput
                id="phoneNumber"
                label="Phone"
                value={get(company, `phoneNumber.phoneNumber`, "")}
                error={get(errors, `phoneNumber.phoneNumber`, null)}
                onChange={(e) => handleInputChange("company.phoneNumber.phoneNumber", e.target.value)}
                errorText="Invalid"
                placeholder="Enter contact phone"
                disabled={disableInputs === true}
                phone={true}
            />
            <BSInput
                id="webUrl"
                label="COMPANY URL"
                placeholder="Enter company url"
                value={company ? company.webUrl : null}
                error={get(errors, `licenseUrl`, null)}
                onChange={(e) => handleInputChange("company.webUrl", e.target.value)}
                disabled={disableInputs === true}
            />
            <BSInput
                id="comments"
                label="COMMENTS"
                placeholder="Enter comments"
                value={company ? company.comments : null}
                multiline
                rows={8}
                margin="normal"
                variant="outlined"
                onChange={(e) => handleInputChange("company.comments", e.target.value)}
            />
        </React.Fragment>
    );
};

export default CompanyForm;
